import { api } from "../http";
import type { ReferenceRecord } from "../../../components/reference/types";
import { buildReferenceCacheKey, isNavigatorOffline, searchCachedReferences } from "../../offline/referenceCache";

export type HazardousReferenceType =
  | "substances"
  | "hazard-codes"
  | "hazard-classes"
  | "hazard-groups"
  | "importers"
  | "exporters";

function buildQuery(params: Record<string, unknown>) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
}

const prefixFor = (type: HazardousReferenceType) => buildReferenceCacheKey("hazardous", type);

function path(type: HazardousReferenceType) {
  return `/api/hazardous/references/${type}`;
}

export const hazardousReferencesApi = {
  async search(type: HazardousReferenceType, params: { q?: string; limit?: number } = {}) {
    if (isNavigatorOffline()) {
      const cached = await searchCachedReferences(prefixFor(type), params);
      if (cached) {
        return cached;
      }
    }
    try {
      const query = buildQuery(params);
      return await api.get<ReferenceRecord[]>(`${path(type)}${query ? `?${query}` : ""}`);
    } catch (error) {
      const cached = await searchCachedReferences(prefixFor(type), params);
      if (cached) {
        return cached;
      }
      throw error;
    }
  },
  async create(type: HazardousReferenceType, payload: Record<string, string | undefined>) {
    return api.post<ReferenceRecord, Record<string, string | undefined>>(path(type), payload);
  },
  async update(type: HazardousReferenceType, id: string, payload: Record<string, string | undefined>) {
    return api.put<ReferenceRecord, Record<string, string | undefined>>(
      `${path(type)}/${encodeURIComponent(id)}`,
      payload,
    );
  },
};
